import { Card, ThemeProvider } from "@mui/material";
import { keepPreviousData } from "@tanstack/react-query";
import LoadingScreen from "components/loading/LoadingScreen";
import Table from "components/table/Table";
import { ALL_OPTION } from "constant";
import { useModalState } from "hooks/useModalState";
import { usePagination } from "hooks/usePagination";
import moment from "moment";
import { useCallback, useMemo, useState } from "react";
import {
  useExportListInboundCall,
  useGetCountListInboundCall,
  useGetListInboundCall,
} from "service/query/inbound.query";
import muiTheme from "theme/muiTheme";

import InboundCallFilter from "./components/InboundCallFilter";
import ModalInboundCalllDetail from "./components/ModalInboundCalllDetail";

const InboundCall = () => {
  const [where, setWhere] = useState(null);
  const [contactCode, setContactCode] = useState(null);

  const {
    page,
    pageSize,
    limit,
    skip,
    handleChangePage,
    handleChangePageSize,
  } = usePagination();

  const handleSubmit = (values) => {
    const and = [
      {
        startedAt: {
          gte: moment(values.dateRange[0]).format("YYYY-MM-DD HH:mm"),
        },
      },
      {
        startedAt: {
          lte: moment(values.dateRange[1]).format("YYYY-MM-DD HH:mm"),
        },
      },
    ];

    // thời lượng trả lời, đơn vị answeredDuration trong options là phút
    if (values.answeredDuration !== ALL_OPTION.value) {
      const [answeredDurationGte, answeredDurationLte] =
        values.answeredDuration === ALL_OPTION.value
          ? [null, null]
          : values.answeredDuration.split("-");

      if (answeredDurationGte)
        and.push({
          answeredDuration: { gte: +answeredDurationGte * 60 },
        });
      if (answeredDurationLte)
        and.push({
          answeredDuration: { lt: +answeredDurationLte * 60 },
        });
    }

    let toNumber = {
      inq: Array.isArray(values.toNumber) ? values.toNumber : [values.toNumber],
    };
    if (values.toNumber === "other") toNumber = "other";
    if (values.toNumber === ALL_OPTION.value) toNumber = "all";
    if (
      Array.isArray(values.toNumber) &&
      values.toNumber.includes(ALL_OPTION.value)
    )
      toNumber = null;

    setContactCode(
      values.contactCode === ALL_OPTION.value ? null : values.contactCode
    );
    setWhere({
      ...(values.fromNumber && { fromNumber: values.fromNumber }),
      ...(values.ladingCode && { ladingCode: values.ladingCode }),
      ...(toNumber && { toNumber }),
      ...(values.agentId !== ALL_OPTION.value && {
        agentId: { like: values.agentId },
      }),
      ...(values.status !== ALL_OPTION.value && {
        status:
          values.status === "missedNoExtension" ? "missed" : values.status,
      }),
      ...(values.extensionId && {
        extensionId:
          values.status === "missedNoExtension"
            ? { eq: "" }
            : values.extensionId, // TODO hỏi BE logic extension
      }),
      ...(values.ivrExtension !== ALL_OPTION.value && {
        ivrExtension: values.ivrExtension,
      }),
      and,
    });
    handleChangePage(1);
  };

  const modalDetail = useModalState();
  const [currentRecord, setCurrentRecord] = useState(null);

  const handleViewRecordDetail = useCallback(
    (record) => {
      modalDetail.openModal();
      setCurrentRecord(record);
    },
    [modalDetail]
  );

  const columns = useMemo(
    () => [
      {
        id: "fromNumber",
        name: "Số điện thoại gọi",
        type: "phone",
      },
      {
        id: "toNumber",
        name: "Số điện thoại nhận",
      },
      {
        id: "ladingCode",
        name: "Số hiệu bưu gửi",
      },
      {
        id: "agentUsername",
        name: "Tài khoản",
        render: (record) => record.agent?.user?.username,
      },
      {
        id: "extension",
        name: "Máy lẻ",
        render: (record) =>
          record.extension
            ? [record.extension.name, record.extension.username]
                .filter((item) => item)
                .join(" - ")
            : "",
      },
      {
        id: "startedAt",
        name: "Thời gian gọi",
        type: "datetime",
      },
      {
        id: "status",
        name: "Trạng thái",
      },
      {
        id: "callStatus",
        name: "Mã trạng thái",
      },
      {
        id: "ivrExtension",
        name: "Nhánh",
      },
      {
        id: "dvtn",
        name: "Đơn vị tiếp nhận",
        render: (record) => {
          return record.contactName || record.subOrgName;
        },
      },
    ],
    []
  );

  const { data: inboundCalls, isFetching } = useGetListInboundCall(
    {
      contactCode,
      filter: {
        order: "startedAt DESC",
        limit,
        skip,
        where,
      },
    },
    {
      enabled: !!where,
      placeholderData: keepPreviousData,
    }
  );

  const { data: count } = useGetCountListInboundCall(
    { contactCode, where },
    { enabled: !!where }
  );

  const { refetch: exportListInboundCall, isFetching: isFetchingExport } =
    useExportListInboundCall(
      { contactCode, filter: { where } },
      {
        enabled: false,
      }
    );

  const handleCloseModalDetail = () => {
    modalDetail.closeModal();
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <Card sx={{ mb: 2 }}>
        <InboundCallFilter
          onSubmit={handleSubmit}
          onExport={exportListInboundCall}
          isDisabledExport={!where}
        />
      </Card>

      <Card>
        <Table
          columns={columns}
          data={inboundCalls}
          toolbar={{
            title: "Danh sách cuộc gọi vào",
          }}
          actionColumn={{
            onView: handleViewRecordDetail,
          }}
          pagination={{
            page,
            pageSize,
            totalItems: count,
            onPageChange: handleChangePage,
            onPageSizeChange: handleChangePageSize,
          }}
        />
      </Card>

      <ModalInboundCalllDetail
        open={modalDetail.open}
        onClose={handleCloseModalDetail}
        currentRecord={currentRecord}
      />
      <LoadingScreen loading={isFetching || isFetchingExport} />
    </ThemeProvider>
  );
};

export default InboundCall;
